/**
 * Error Types and Interfaces for NovaQ Finance System
 * 
 * This file defines standardized error types and interfaces
 * for consistent error handling across the application.
 */

// Base error interface
export interface BaseError {
  code: string;
  message: string;
  timestamp: Date;
  context?: Record<string, any>;
}

// API Error types
export interface ApiError extends BaseError {
  status: number;
  statusText: string;
  endpoint: string;
  method: string;
  response?: any;
}

// Authentication Error types
export interface AuthError extends BaseError {
  type: 'token_expired' | 'token_invalid' | 'unauthorized' | 'forbidden';
  redirectTo?: string;
}

// Validation Error types
export interface ValidationError extends BaseError {
  field: string;
  value: any;
  rule: string;
  expected?: any;
}

// Network Error types
export interface NetworkError extends BaseError {
  type: 'timeout' | 'connection_failed' | 'server_unavailable';
  url: string;
  retryable: boolean;
}

// Business Logic Error types
export interface BusinessError extends BaseError {
  category: 'customer' | 'accountant' | 'tax' | 'insurance' | 'report' | 'system';
  operation: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

// Union type for all error types
export type AppError = ApiError | AuthError | ValidationError | NetworkError | BusinessError;

// Error codes enum
export enum ErrorCodes {
  // Authentication errors
  TOKEN_EXPIRED = 'AUTH_TOKEN_EXPIRED',
  TOKEN_INVALID = 'AUTH_TOKEN_INVALID',
  UNAUTHORIZED = 'AUTH_UNAUTHORIZED',
  FORBIDDEN = 'AUTH_FORBIDDEN',
  
  // API errors
  API_ERROR = 'API_ERROR',
  API_TIMEOUT = 'API_TIMEOUT',
  API_NETWORK_ERROR = 'API_NETWORK_ERROR',
  API_SERVER_ERROR = 'API_SERVER_ERROR',
  
  // Validation errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  REQUIRED_FIELD = 'VALIDATION_REQUIRED_FIELD',
  INVALID_FORMAT = 'VALIDATION_INVALID_FORMAT',
  INVALID_LENGTH = 'VALIDATION_INVALID_LENGTH',
  
  // Business logic errors
  CUSTOMER_NOT_FOUND = 'BUSINESS_CUSTOMER_NOT_FOUND',
  ACCOUNTANT_NOT_FOUND = 'BUSINESS_ACCOUNTANT_NOT_FOUND',
  DUPLICATE_ENTRY = 'BUSINESS_DUPLICATE_ENTRY',
  OPERATION_FAILED = 'BUSINESS_OPERATION_FAILED',
  
  // System errors
  UNKNOWN_ERROR = 'SYSTEM_UNKNOWN_ERROR',
  CONFIGURATION_ERROR = 'SYSTEM_CONFIGURATION_ERROR'
}

// Error severity levels
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

// Error categories
export enum ErrorCategory {
  AUTH = 'auth',
  API = 'api',
  VALIDATION = 'validation',
  BUSINESS = 'business',
  SYSTEM = 'system',
  NETWORK = 'network'
}

// Error handler interface
export interface ErrorHandler {
  handle(error: AppError): void;
  canHandle(error: AppError): boolean;
  getPriority(): number;
}

// Error logger interface
export interface ErrorLogger {
  log(error: AppError): void;
  logInfo(message: string, context?: Record<string, any>): void;
  logWarning(message: string, context?: Record<string, any>): void;
  logError(error: AppError): void;
}

// Error recovery interface
export interface ErrorRecovery {
  canRecover(error: AppError): boolean;
  recover(error: AppError): Promise<boolean>;
  getRecoveryOptions(error: AppError): string[];
}

// Error context interface
export interface ErrorContext {
  userId?: string;
  sessionId?: string;
  requestId?: string;
  userAgent?: string;
  url?: string;
  timestamp: Date;
  additional?: Record<string, any>;
}

// Error response interface for API calls
export interface ErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: any;
    timestamp: string;
    requestId?: string;
  };
}

// Success response interface for API calls
export interface SuccessResponse<T = any> {
  success: true;
  data: T;
  message?: string;
  timestamp: string;
  requestId?: string;
}

// Union type for API responses
export type ApiResponse<T = any> = SuccessResponse<T> | ErrorResponse;

// Error boundary props
export interface ErrorBoundaryProps {
  fallback?: React.ComponentType<{ error: AppError; retry: () => void }>;
  onError?: (error: AppError, errorInfo: React.ErrorInfo) => void;
  children: React.ReactNode;
}

// Error boundary state
export interface ErrorBoundaryState {
  hasError: boolean;
  error?: AppError;
  errorInfo?: React.ErrorInfo;
}
